import React, { useState } from 'react';
import { ArrowRight, ChevronDown, Pause, Play, SkipForward } from 'lucide-react';
import { Card } from '../SubscriptionAppCard';
import { Button } from '../SubscriptionAppButton';
import { Badge } from '../SubscriptionAppBadge';
import type { ContactSubscription } from '../../data/mockContactSubscriptions';
import { formatShortDate } from '../../utils/dateFormat';
interface ContactSubscriptionsTabProps {
  subscriptions: ContactSubscription[];
  onView: (subscriptionId: string) => void;
  onSkipNext: (subscriptionId: string) => void;
  onTogglePause: (subscriptionId: string) => void;
}
type PendingAction = {
  id: string;
  kind: 'skip' | 'pause';
} | null;
function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const next = new Date(y, m - 1 + months, d);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
}
function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

// One card per current subscription, with past subscriptions collapsed underneath
export function ContactSubscriptionsTab({
  subscriptions,
  onView,
  onSkipNext,
  onTogglePause
}: ContactSubscriptionsTabProps) {
  const [pending, setPending] = useState<PendingAction>(null);
  const [showPast, setShowPast] = useState(false);
  const current = subscriptions.filter(sub => sub.status !== 'Cancelled');
  const past = subscriptions.filter(sub => sub.status === 'Cancelled');
  const confirm = () => {
    if (!pending) return;
    if (pending.kind === 'skip') onSkipNext(pending.id);else onTogglePause(pending.id);
    setPending(null);
  };
  return <div className="space-y-6" data-id="element-1520" data-mp="35jb7oi759r2v">
      {current.length === 0 && <Card data-id="element-1521" data-mp="2rj0gtf5u4chp">
          <p className="text-sm text-[#64748B] text-center py-6" data-id="element-1522" data-mp="2rii83j1zk5sw">
            No current subscriptions.
          </p>
        </Card>}

      {current.map(sub => {
      const isPaused = sub.status === 'Paused';
      const isPending = pending?.id === sub.id;
      const details = [{
        label: 'Frequency',
        value: sub.frequency
      }, {
        label: 'Next billing',
        value: isPaused ? 'Paused' : sub.nextBilling ? formatShortDate(sub.nextBilling) : '—'
      }, {
        label: 'Bottles per delivery',
        value: String(sub.bottles)
      }, {
        label: 'Price',
        value: `$${sub.price.toFixed(2)}`
      }, {
        label: 'Skips & pauses (12 months)',
        value: `${plural(sub.skips12m, 'skip')} · ${plural(sub.pauses12m, 'pause')}`
      }];
      return <Card key={sub.id} padding="none" data-id="element-1523" data-mp="1bzaudc0dunzp">
            <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white flex flex-wrap items-start justify-between gap-4" data-id="element-1524" data-mp="1bxzjks8isfz3">
              <div className="min-w-0" data-id="element-1525" data-mp="l6ujp7465mq">
                <div className="flex flex-wrap items-center gap-3" data-id="element-1526" data-mp="m8defudfwf0">
                  <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-1527" data-mp="3rqoxt9v57r82">
                    {sub.club}
                  </h3>
                  <Badge variant={isPaused ? 'warning' : 'success'} data-id="element-1528" data-mp="27ah7e926q8es">
                    {sub.status}
                  </Badge>
                </div>
                <p className="text-sm text-[#64748B] mt-1" data-id="element-1529" data-mp="2g9uh92u80z01">
                  {sub.plan} · {sub.id}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-id="element-1530" data-mp="2l144xxw7ls9a">
                <Button variant="outline" size="sm" iconLeft={<SkipForward className="w-4 h-4" data-id="element-1532" data-mp="2pz5o0itmxrkr" />} disabled={isPaused || !sub.nextBilling} onClick={() => setPending({
              id: sub.id,
              kind: 'skip'
            })} data-id="element-1531" data-mp="2pzx9v1xfkaxu">
                  Skip next
                </Button>
                {isPaused ? <Button variant="outline" size="sm" iconLeft={<Play className="w-4 h-4" data-id="element-1534" data-mp="33ydpxjh6ndsn" />} onClick={() => onTogglePause(sub.id)} data-id="element-1533" data-mp="33zosjfyw6fi6">
                    Resume
                  </Button> : <Button variant="outline" size="sm" iconLeft={<Pause className="w-4 h-4" data-id="element-1536" data-mp="19m70pgrgewhc" />} onClick={() => setPending({
              id: sub.id,
              kind: 'pause'
            })} data-id="element-1535" data-mp="15lwuc2zr241y">
                    Pause
                  </Button>}
                <Button variant="primary" size="sm" iconRight={<ArrowRight className="w-4 h-4" data-id="element-1538" data-mp="abqbtwu4c5z0" />} onClick={() => onView(sub.id)} data-id="element-1537" data-mp="a8cbsv8xg8uq">
                  View subscription
                </Button>
              </div>
            </div>

            {isPending && <div className="px-6 py-3 bg-[#FEF3C7] border-b border-[#FDE68A] flex flex-wrap items-center justify-between gap-3" data-id="element-1539" data-mp="j893z6uizpuj">
                <p className="text-sm font-medium text-[#92400E]" data-id="element-1540" data-mp="3mrarp3s1l5w7">
                  {pending?.kind === 'skip' && sub.nextBilling ? `Skip the ${formatShortDate(sub.nextBilling)} delivery? Next billing moves to ${formatShortDate(addMonths(sub.nextBilling, sub.intervalMonths))}.` : `Pause ${sub.club}? No deliveries or charges until it's resumed.`}
                </p>
                <div className="flex items-center gap-2" data-id="element-1541" data-mp="38x1b03q41nds">
                  <Button variant="outline" size="sm" onClick={() => setPending(null)} data-id="element-1542" data-mp="33zhxx755fgnf">
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={confirm} data-id="element-1543" data-mp="1jgfhlzaerktc">
                    {pending?.kind === 'skip' ? 'Skip delivery' : 'Pause subscription'}
                  </Button>
                </div>
              </div>}

            <dl className="p-6 bg-white rounded-b-lg grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-1544" data-mp="1einudikpkmvh">
              {details.map(item => <div key={item.label} data-id="element-1545" data-mp="229t7vr36f32m">
                  <dt className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-1" data-id="element-1546" data-mp="226nieaqmxz6d">
                    {item.label}
                  </dt>
                  <dd className="text-sm font-medium text-[#0F172A]" data-id="element-1547" data-mp="1t70f1jecijao">
                    {item.value}
                  </dd>
                </div>)}
            </dl>
          </Card>;
    })}

      {past.length > 0 && <Card padding="none" data-id="element-1548" data-mp="hrv8g4b3g0ls">
          <button type="button" onClick={() => setShowPast(open => !open)} aria-expanded={showPast} className="w-full p-6 flex items-center justify-between text-left" data-id="element-1549" data-mp="hrlmcrt66dld">
            <span className="text-base font-semibold text-[#0F172A]" data-id="element-1550" data-mp="2og43m0un8in5">
              Past subscriptions ({past.length})
            </span>
            <ChevronDown className={`w-5 h-5 text-[#94A3B8] transition-transform ${showPast ? 'rotate-180' : ''}`} data-id="element-1551" data-mp="2jk5gegl1x6ob" />
          </button>
          {showPast && <ul className="border-t border-[#E2E8F0] divide-y divide-[#F1F5F9]" data-id="element-1552" data-mp="2aqk2t0kmhvgh">
              {past.map(sub => <li key={sub.id} className="px-6 py-4 flex flex-wrap items-center justify-between gap-3" data-id="element-1553" data-mp="25qxo0mp5c75k">
                  <div className="min-w-0" data-id="element-1554" data-mp="20qjet1bt6r5y">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-1555" data-mp="20txjwhbuxeh3">
                      <span className="text-sm font-semibold text-[#0F172A]" data-id="element-1556" data-mp="1vt01q4qukkfl">
                        {sub.club}
                      </span>
                      <Badge variant="error" data-id="element-1557" data-mp="3q6it0wuab6sg">Cancelled</Badge>
                    </div>
                    <p className="text-sm text-[#64748B] mt-0.5" data-id="element-1558" data-mp="3q46fcvvk7il7">
                      {sub.plan}
                      {sub.cancelledOn ? ` · Cancelled ${formatShortDate(sub.cancelledOn)}` : ''}
                      {sub.cancelReason ? ` · ${sub.cancelReason}` : ''}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" iconRight={<ArrowRight className="w-4 h-4" data-id="element-1560" data-mp="1hnhn0vnd4tac" />} onClick={() => onView(sub.id)} data-id="element-1559" data-mp="1hn7vv4ql0g32">
                    View subscription
                  </Button>
                </li>)}
            </ul>}
        </Card>}
    </div>;
}